/**
 * _save_status.js — the small "Unsaved / Saving / Saved" marker beside a settings section.
 *
 * Settings save as they are edited, so the marker is the only feedback a save
 * gives: no toast on success. It sits beside the heading of the section being
 * edited, so the confirmation appears next to what changed rather than at the
 * foot of a long tab. Two section layouts are recognised: a `.settings-category`
 * whose first `.d-flex` child holds the h3, and a `.settings-section` with an
 * `.settings-section-title` heading (Region & Tax). A caller can instead pass an
 * explicit element to render into, which the forms that keep a Save button use
 * to repeat the marker beside that button.
 *
 * Imported by autosave_controller.js and settings_controller.js (entity colours).
 * Styles: static/css/settings.css (.save-status).
 */

const SAVED_VISIBLE_MS = 3000;

const STATES = {
    dirty: { icon: 'bi-circle-fill', text: 'Unsaved changes' },
    saving: { icon: 'spinner', text: 'Saving…' },
    saved: { icon: 'bi-check-circle-fill', text: 'Saved' },
    failed: { icon: 'bi-exclamation-circle-fill', text: 'Not saved' },
};

/**
 * The marker for the section containing `element`, created on first use.
 * Returns null when the element is in no section header layout and no
 * explicit slot is given; callers treat that as "no marker to show".
 */
export function saveStatusFor(element, slot = null) {
    const place = slot ? { host: slot, before: null } : placeFor(element);
    if (!place) return null;
    let marker = place.host.querySelector(':scope > .save-status');
    if (!marker) {
        marker = document.createElement('span');
        marker.className = 'save-status';
        marker.setAttribute('role', 'status');
        marker.setAttribute('aria-live', 'polite');
        marker.hidden = true;
        place.host.insertBefore(marker, place.before);
    }
    return marker;
}

/**
 * Render `state` (a STATES key) into a marker. `detail` replaces the default
 * text for a failure; `retry` adds a Retry button that calls it, and
 * `action` ({label, run}) a button of its own label.
 */
export function showSaveStatus(marker, state, { detail = null, retry = null, action = null } = {}) {
    if (!marker) return;
    clearTimeout(marker._hideTimer);
    const spec = STATES[state];
    marker.dataset.state = state;
    marker.replaceChildren(iconFor(spec.icon), document.createTextNode(detail || spec.text));
    if (retry) marker.appendChild(markerButton('Retry', retry));
    if (action) marker.appendChild(markerButton(action.label, action.run));
    marker.hidden = false;
    if (state === 'saved') {
        marker._hideTimer = setTimeout(() => { marker.hidden = true; }, SAVED_VISIBLE_MS);
    }
}

export function clearSaveStatus(marker) {
    if (!marker) return;
    clearTimeout(marker._hideTimer);
    marker.hidden = true;
}

// Where the marker goes for the section containing `element`: inside a
// `.settings-section-title` after its text, or in a category's header row
// directly after the heading (so a row that also holds a button keeps the
// marker next to that button, not wedged between heading and button).
// Null outside a known section layout.
function placeFor(element) {
    const section = element.closest('.settings-section, .settings-category');
    if (!section) return null;
    if (section.classList.contains('settings-section')) {
        const title = section.querySelector(':scope > .settings-section-title');
        return title ? { host: title, before: null } : null;
    }
    const row = section.querySelector(':scope > .d-flex');
    return row && row.firstElementChild ? { host: row, before: row.firstElementChild.nextSibling } : null;
}

function iconFor(name) {
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.className = name === 'spinner'
        ? 'spinner-border spinner-border-sm'
        : 'bi ' + name;
    return icon;
}

function markerButton(label, run) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-link btn-sm p-0 save-status-retry';
    button.textContent = label;
    button.addEventListener('click', run, { once: true });
    return button;
}
